import { useState, useMemo } from "react";
import {
  Home,
  Building2,
  Hotel,
  Bed,
  Bath,
  Flame,
  Droplets,
  Zap,
  FuelIcon,
  Warehouse,
  PackageOpen,
  BedDouble,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  Sparkles,
  Clock,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";

/* ---------- Pricing logic (real C4 rates, ex-VAT) ---------- */
const RECOMMENDED_TYPE = (answers) => {
  const bed = answers.bedrooms === "5+" ? 5 : parseInt(answers.bedrooms || "0", 10) || 0;
  const bath = answers.bathrooms === "3+" ? 3 : parseInt(answers.bathrooms || "1", 10);
  if (bath >= 2 || bed >= 4) return "system";
  return "combi";
};

/**
 * Brackets based on real C4 pricing (ex-VAT):
 *  - swap (same type, same location):   £1,900 – £2,500
 *  - moved  (same type, new location):  £2,250 – £3,000
 *  - system_change (different type/new install): £3,000 – £4,500
 */
const computeQuote = (answers) => {
  const recommended = RECOMMENDED_TYPE(answers);
  const current = answers.current_boiler;
  const isNewInstall = current === "none";
  const isSystemChange = isNewInstall || (current && current !== recommended);
  const isMoved = !!answers.new_location;

  let bracket, min, max;
  if (isSystemChange) {
    bracket = "system_change";
    min = 3000;
    max = 4500;
  } else if (isMoved) {
    bracket = "moved";
    min = 2250;
    max = 3000;
  } else {
    bracket = "swap";
    min = 1900;
    max = 2500;
  }

  return { min, max, type: isNewInstall ? recommended : current || recommended, bracket };
};

const BRACKET_LABEL = {
  swap: "Straight swap · same location",
  moved: "Replacement · moved to new location",
  system_change: "System change / new install",
};

/* ---------- Step config ---------- */
const steps = [
  {
    id: "property_type",
    title: "What type of property do you live in?",
    help: "This helps us understand the heating demand and installation access.",
    type: "options",
    options: [
      { v: "house", l: "House", icon: Home },
      { v: "flat", l: "Flat / Apartment", icon: Building2 },
      { v: "bungalow", l: "Bungalow", icon: Hotel },
    ],
  },
  {
    id: "bedrooms",
    title: "How many bedrooms?",
    help: "Including any box rooms currently used as a bedroom.",
    type: "options",
    options: [
      { v: "1", l: "1 bedroom", icon: Bed },
      { v: "2", l: "2 bedrooms", icon: Bed },
      { v: "3", l: "3 bedrooms", icon: Bed },
      { v: "4", l: "4 bedrooms", icon: BedDouble },
      { v: "5+", l: "5 or more", icon: BedDouble },
    ],
  },
  {
    id: "bathrooms",
    title: "How many bathrooms or showers?",
    help: "Count each room that has a bath or shower (en-suites included).",
    type: "options",
    options: [
      { v: "1", l: "1", icon: Bath },
      { v: "2", l: "2", icon: Bath },
      { v: "3+", l: "3 or more", icon: Bath },
    ],
  },
  {
    id: "current_boiler",
    title: "What type of boiler do you have now?",
    help: "Not sure? Pick the closest — we'll confirm on our free site visit.",
    type: "options",
    options: [
      {
        v: "combi",
        l: "Combi",
        sub: "One wall-mounted unit, no tank",
        icon: Flame,
      },
      {
        v: "system",
        l: "System",
        sub: "Boiler + hot water cylinder",
        icon: Flame,
      },
      {
        v: "conventional",
        l: "Conventional",
        sub: "Boiler + cylinder + loft tanks",
        icon: Flame,
      },
      {
        v: "none",
        l: "New install",
        sub: "No boiler yet / new build",
        icon: Sparkles,
      },
    ],
  },
  {
    id: "fuel_type",
    title: "What fuel does your boiler use?",
    help: "Most UK homes use mains gas.",
    type: "options",
    options: [
      { v: "mains_gas", l: "Mains Gas", icon: Flame },
      { v: "lpg", l: "LPG", icon: FuelIcon },
      { v: "oil", l: "Oil", icon: Droplets },
      { v: "electric", l: "Electric", icon: Zap },
    ],
  },
  {
    id: "boiler_location",
    title: "Where is your boiler located?",
    help: "Or where would you like the new one fitted?",
    type: "options",
    options: [
      { v: "kitchen", l: "Kitchen", icon: Home },
      { v: "airing_cupboard", l: "Airing cupboard", icon: PackageOpen },
      { v: "garage", l: "Garage", icon: Warehouse },
      { v: "loft", l: "Loft", icon: Home },
      { v: "other", l: "Other / unsure", icon: PackageOpen },
    ],
    extra: "new_location_checkbox",
  },
  {
    id: "brand_preference",
    title: "Any brand preference?",
    help: "We install Vaillant and Glow-worm — both highly reliable with long manufacturer warranties.",
    type: "options",
    options: [
      {
        v: "vaillant",
        l: "Vaillant",
        sub: "Premium, up to 10-year warranty",
        icon: ShieldCheck,
      },
      {
        v: "glowworm",
        l: "Glow-worm",
        sub: "Great value, up to 10-year warranty",
        icon: ShieldCheck,
      },
      {
        v: "no_preference",
        l: "No preference",
        sub: "Let us recommend the best fit",
        icon: Sparkles,
      },
    ],
  },
  {
    id: "contact",
    title: "Where shall we send your quote?",
    help: "We'll reply within one working day with next steps.",
    type: "contact",
  },
];

/* ---------- Shared progress ---------- */
function ProgressBar({ step, total }) {
  const pct = Math.round(((step + 1) / total) * 100);
  return (
    <div className="flex items-center gap-4">
      <div className="text-xs font-heading tracking-[0.2em] uppercase text-[#C9A24A] font-semibold">
        Step {step + 1} of {total}
      </div>
      <div className="flex-1 h-[2px] bg-[#E8E5DD] relative overflow-hidden">
        <div
          data-testid="boiler-progress-fill"
          className="absolute inset-y-0 left-0 bg-[#C9A24A] transition-[width] duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="text-xs font-heading text-[#5C6670]">{pct}%</div>
    </div>
  );
}

/* ---------- Option card ---------- */
function OptionCard({ opt, selected, onSelect, testid }) {
  const Icon = opt.icon;
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => onSelect(opt.v)}
      className={cn(
        "group relative text-left p-6 bg-white border transition-all duration-200 hover:border-[#C9A24A] hover:-translate-y-0.5",
        selected
          ? "border-[#C9A24A] ring-2 ring-[#C9A24A]/20"
          : "border-[#E8E5DD]"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="w-11 h-11 grid place-items-center bg-[#F5EED8]">
          <Icon size={20} strokeWidth={1.5} className="text-[#C9A24A]" />
        </div>
        {selected && (
          <CheckCircle2
            size={18}
            strokeWidth={1.75}
            className="text-[#C9A24A]"
          />
        )}
      </div>
      <div className="mt-5 font-heading text-lg text-[#1C2A39] font-semibold">
        {opt.l}
      </div>
      {opt.sub && (
        <div className="mt-1 text-xs text-[#5C6670] leading-relaxed">
          {opt.sub}
        </div>
      )}
    </button>
  );
}

/* ---------- Contact step ---------- */
function ContactStep({ answers, setField, onNext, onBack, submitting }) {
  const [localErrors, setLocalErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!answers.name || answers.name.length < 2) errs.name = "Please enter your full name";
    if (!answers.email || !/^\S+@\S+\.\S+$/.test(answers.email))
      errs.email = "Please enter a valid email";
    if (!answers.phone || answers.phone.length < 7) errs.phone = "Please enter a phone number";
    if (!answers.postcode || answers.postcode.length < 2)
      errs.postcode = "Please enter your postcode";
    setLocalErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handle = () => {
    if (validate()) onNext();
  };

  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label className="font-heading text-xs uppercase tracking-[0.18em] text-[#1C2A39]">
            Full name
          </Label>
          <Input
            data-testid="boiler-input-name"
            className="mt-2 rounded-sm border-[#E8E5DD] bg-white h-11 focus-visible:ring-[#C9A24A]"
            value={answers.name || ""}
            onChange={(e) => setField("name", e.target.value)}
            placeholder="Jane Smith"
          />
          {localErrors.name && (
            <p className="mt-1 text-xs text-destructive">{localErrors.name}</p>
          )}
        </div>
        <div>
          <Label className="font-heading text-xs uppercase tracking-[0.18em] text-[#1C2A39]">
            Postcode
          </Label>
          <Input
            data-testid="boiler-input-postcode"
            className="mt-2 rounded-sm border-[#E8E5DD] bg-white h-11 focus-visible:ring-[#C9A24A]"
            value={answers.postcode || ""}
            onChange={(e) => setField("postcode", e.target.value.toUpperCase())}
            placeholder="SO15 1AA"
          />
          {localErrors.postcode && (
            <p className="mt-1 text-xs text-destructive">{localErrors.postcode}</p>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label className="font-heading text-xs uppercase tracking-[0.18em] text-[#1C2A39]">
            Email
          </Label>
          <Input
            data-testid="boiler-input-email"
            type="email"
            className="mt-2 rounded-sm border-[#E8E5DD] bg-white h-11 focus-visible:ring-[#C9A24A]"
            value={answers.email || ""}
            onChange={(e) => setField("email", e.target.value)}
            placeholder="you@example.co.uk"
          />
          {localErrors.email && (
            <p className="mt-1 text-xs text-destructive">{localErrors.email}</p>
          )}
        </div>
        <div>
          <Label className="font-heading text-xs uppercase tracking-[0.18em] text-[#1C2A39]">
            Phone
          </Label>
          <Input
            data-testid="boiler-input-phone"
            className="mt-2 rounded-sm border-[#E8E5DD] bg-white h-11 focus-visible:ring-[#C9A24A]"
            value={answers.phone || ""}
            onChange={(e) => setField("phone", e.target.value)}
            placeholder="07123 456 789"
          />
          {localErrors.phone && (
            <p className="mt-1 text-xs text-destructive">{localErrors.phone}</p>
          )}
        </div>
      </div>

      <div>
        <Label className="font-heading text-xs uppercase tracking-[0.18em] text-[#1C2A39]">
          Preferred install date{" "}
          <span className="text-[#5C6670] normal-case tracking-normal">(optional)</span>
        </Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              data-testid="boiler-date-trigger"
              className={cn(
                "mt-2 h-11 justify-start font-normal rounded-sm border-[#E8E5DD] bg-white text-[#1C2A39] hover:bg-white hover:border-[#C9A24A] w-full sm:w-auto",
                !answers.preferred_date && "text-[#5C6670]"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4 text-[#C9A24A]" strokeWidth={1.5} />
              {answers.preferred_date
                ? format(answers.preferred_date, "PPP")
                : "Pick a preferred date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-white border-[#E8E5DD]" align="start">
            <Calendar
              mode="single"
              selected={answers.preferred_date}
              onSelect={(d) => setField("preferred_date", d)}
              disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      <div>
        <Label className="font-heading text-xs uppercase tracking-[0.18em] text-[#1C2A39]">
          Anything else we should know?{" "}
          <span className="text-[#5C6670] normal-case tracking-normal">(optional)</span>
        </Label>
        <Textarea
          data-testid="boiler-input-notes"
          rows={3}
          className="mt-2 rounded-sm border-[#E8E5DD] bg-white focus-visible:ring-[#C9A24A] resize-none"
          value={answers.notes || ""}
          onChange={(e) => setField("notes", e.target.value)}
          placeholder="E.g. boiler is making noise, or we're adding a second bathroom."
        />
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-[#E8E5DD]">
        <Button
          type="button"
          variant="outline"
          data-testid="boiler-back-button"
          onClick={onBack}
          className="rounded-sm border-[#1C2A39] text-[#1C2A39] hover:bg-[#1C2A39] hover:text-white h-11 px-5"
        >
          <ArrowLeft size={16} strokeWidth={1.75} className="mr-2" />
          Back
        </Button>
        <Button
          type="button"
          data-testid="boiler-submit-button"
          disabled={submitting}
          onClick={handle}
          className="bg-[#C9A24A] hover:bg-[#B0893A] text-white rounded-sm h-12 px-7 font-heading font-semibold tracking-wide"
        >
          {submitting ? "Preparing quote..." : (
            <>
              See my indicative quote
              <ArrowRight size={16} strokeWidth={1.75} className="ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

/* ---------- Result ---------- */
function ResultScreen({ answers, quote, onReset }) {
  const brandLabel =
    answers.brand_preference === "vaillant"
      ? "Vaillant"
      : answers.brand_preference === "glowworm"
      ? "Glow-worm"
      : "Vaillant or Glow-worm";
  const incMin = Math.round(quote.min * 1.2);
  const incMax = Math.round(quote.max * 1.2);
  const bracketLabel = BRACKET_LABEL[quote.bracket] || "Boiler installation";
  return (
    <div data-testid="boiler-result-screen" className="text-center">
      <div className="mx-auto w-14 h-14 grid place-items-center bg-[#C9A24A]/10 border border-[#C9A24A]/30">
        <CheckCircle2 size={26} strokeWidth={1.5} className="text-[#C9A24A]" />
      </div>
      <div className="mt-6 uppercase text-xs tracking-[0.3em] text-[#C9A24A] font-semibold">
        Indicative Fixed-Price Range
      </div>
      <div
        data-testid="boiler-result-price"
        className="mt-4 font-heading text-5xl lg:text-6xl text-[#1C2A39] font-medium tracking-tight"
      >
        £{quote.min.toLocaleString()} – £{quote.max.toLocaleString()}
        <span className="ml-2 align-middle text-base lg:text-lg text-[#5C6670] font-normal">
          + VAT
        </span>
      </div>
      <div className="mt-2 text-sm text-[#5C6670]">
        ({bracketLabel}) · approx.{" "}
        <span className="text-[#1C2A39] font-semibold">
          £{incMin.toLocaleString()} – £{incMax.toLocaleString()}
        </span>{" "}
        inc. VAT, fitted
      </div>

      <div className="mt-10 grid sm:grid-cols-3 gap-px bg-[#E8E5DD] text-left">
        <div className="bg-white p-6">
          <Sparkles size={18} strokeWidth={1.5} className="text-[#C9A24A]" />
          <div className="mt-3 font-heading text-sm font-semibold text-[#1C2A39]">
            Recommended type
          </div>
          <div className="mt-1 text-sm text-[#5C6670] capitalize">
            {quote.type} boiler
          </div>
        </div>
        <div className="bg-white p-6">
          <ShieldCheck size={18} strokeWidth={1.5} className="text-[#C9A24A]" />
          <div className="mt-3 font-heading text-sm font-semibold text-[#1C2A39]">
            Brand fitted
          </div>
          <div className="mt-1 text-sm text-[#5C6670]">
            {brandLabel} · up to 10-year warranty
          </div>
        </div>
        <div className="bg-white p-6">
          <Clock size={18} strokeWidth={1.5} className="text-[#C9A24A]" />
          <div className="mt-3 font-heading text-sm font-semibold text-[#1C2A39]">
            Next step
          </div>
          <div className="mt-1 text-sm text-[#5C6670]">
            Free site survey within 2–3 days
          </div>
        </div>
      </div>

      <p className="mt-8 mx-auto max-w-lg text-xs text-[#5C6670] leading-relaxed">
        This is an <strong>indicative range</strong>, not a binding quote. Your
        fixed-price, itemised quotation will be confirmed after a free site
        survey. We&apos;ve emailed your details to Bailey and the team — we&apos;ll
        reply within one working day.
      </p>

      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Button
          data-testid="boiler-result-cta"
          onClick={() =>
            document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" })
          }
          className="bg-[#1C2A39] hover:bg-[#2A3D52] text-white rounded-sm h-11 px-6 font-heading font-semibold"
        >
          Request a full quotation
        </Button>
        <Button
          data-testid="boiler-result-reset"
          variant="outline"
          onClick={onReset}
          className="rounded-sm border-[#1C2A39] text-[#1C2A39] hover:bg-[#1C2A39] hover:text-white h-11 px-6"
        >
          Start again
        </Button>
      </div>
    </div>
  );
}

/* ---------- Main Section ---------- */
export default function BoilerQuote() {
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [quoteResult, setQuoteResult] = useState(null);

  const step = steps[stepIdx];
  const setField = (k, v) => setAnswers((prev) => ({ ...prev, [k]: v }));

  const pick = (val) => {
    setField(step.id, val);
    // auto-advance a hair later for smoothness
    setTimeout(() => setStepIdx((i) => Math.min(i + 1, steps.length - 1)), 220);
  };

  const goNext = () => setStepIdx((i) => Math.min(i + 1, steps.length - 1));
  const goBack = () => setStepIdx((i) => Math.max(i - 1, 0));

  const onFinish = async () => {
    const quote = computeQuote(answers);
    setSubmitting(true);
    try {
      const payload = {
        property_type: answers.property_type,
        bedrooms: answers.bedrooms,
        bathrooms: answers.bathrooms,
        current_boiler: answers.current_boiler,
        fuel_type: answers.fuel_type,
        boiler_location: answers.boiler_location,
        new_location: !!answers.new_location,
        brand_preference: answers.brand_preference,
        postcode: answers.postcode,
        name: answers.name,
        email: answers.email,
        phone: answers.phone,
        preferred_date: answers.preferred_date
          ? format(answers.preferred_date, "yyyy-MM-dd")
          : null,
        notes: answers.notes || null,
        indicative_min: quote.min,
        indicative_max: quote.max,
        recommended_type: quote.type,
      };
      await api.post("/boiler-quotes", payload);
      setQuoteResult(quote);
      setDone(true);
      toast.success("Your boiler quote is ready", {
        description: "We&apos;ve emailed your details to Bailey and the team.",
      });
    } catch (e) {
      toast.error("Couldn't submit quote", {
        description: e?.response?.data?.detail || "Please try again or call us directly.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setAnswers({});
    setStepIdx(0);
    setDone(false);
    setQuoteResult(null);
  };

  // Auto-finish when contact step submit triggers onNext at last step
  const handleContactNext = () => {
    onFinish();
  };

  const selected = answers[step?.id];
  const isOptionStep = step?.type === "options";
  const showExtra = step?.extra === "new_location_checkbox";

  return (
    <section
      id="boiler"
      data-testid="boiler-section"
      className="py-24 lg:py-32 bg-white border-y border-[#E8E5DD]"
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <span className="uppercase text-xs tracking-[0.3em] text-[#C9A24A] font-semibold">
            Instant Boiler Quote
          </span>
          <h2
            data-testid="boiler-title"
            className="mt-4 font-heading text-3xl lg:text-5xl text-[#1C2A39] font-medium tracking-tight"
          >
            Fixed-price boiler, fitted — in about 90 seconds.
          </h2>
          <p className="mt-5 text-base text-[#5C6670] max-w-2xl mx-auto leading-relaxed">
            Answer a few quick questions and see an indicative fixed-price
            range — from <strong className="text-[#1C2A39]">£1,900 + VAT</strong> for
            a straight swap. We fit <strong>Vaillant</strong> and{" "}
            <strong>Glow-worm</strong> boilers — both Gas Safe installed with
            up to 10-year manufacturer warranties.
          </p>
        </div>

        <div className="bg-[#F9F8F6] border border-[#E8E5DD] p-6 lg:p-10">
          {!done && <ProgressBar step={stepIdx} total={steps.length} />}

          <div className="mt-10 min-h-[360px]">
            {done && quoteResult ? (
              <ResultScreen answers={answers} quote={quoteResult} onReset={reset} />
            ) : (
              <div key={step.id} className="c4-rise">
                <h3
                  data-testid={`boiler-step-title-${step.id}`}
                  className="font-heading text-2xl lg:text-3xl text-[#1C2A39] font-medium"
                >
                  {step.title}
                </h3>
                {step.help && (
                  <p className="mt-3 text-sm text-[#5C6670] leading-relaxed max-w-2xl">
                    {step.help}
                  </p>
                )}

                <div className="mt-8">
                  {isOptionStep && (
                    <>
                      <div
                        className={cn(
                          "grid gap-4",
                          step.options.length <= 3
                            ? "grid-cols-1 sm:grid-cols-3"
                            : step.options.length === 4
                            ? "grid-cols-2 lg:grid-cols-4"
                            : "grid-cols-2 lg:grid-cols-5"
                        )}
                      >
                        {step.options.map((opt) => (
                          <OptionCard
                            key={opt.v}
                            opt={opt}
                            selected={selected === opt.v}
                            onSelect={pick}
                            testid={`boiler-option-${step.id}-${opt.v}`}
                          />
                        ))}
                      </div>
                      {showExtra && (
                        <div className="mt-6 flex items-center gap-3 bg-white border border-[#E8E5DD] p-4">
                          <Checkbox
                            id="new-location"
                            data-testid="boiler-new-location-checkbox"
                            checked={!!answers.new_location}
                            onCheckedChange={(c) => setField("new_location", !!c)}
                            className="data-[state=checked]:bg-[#C9A24A] data-[state=checked]:border-[#C9A24A]"
                          />
                          <Label
                            htmlFor="new-location"
                            className="text-sm text-[#1C2A39] cursor-pointer"
                          >
                            I&apos;d like the boiler moved to a different location
                            <span className="block text-xs text-[#5C6670] mt-0.5">
                              Adds complexity &amp; a small extra cost
                            </span>
                          </Label>
                        </div>
                      )}
                      <div className="mt-8 flex items-center justify-between">
                        <Button
                          type="button"
                          variant="outline"
                          data-testid="boiler-back-button"
                          disabled={stepIdx === 0}
                          onClick={goBack}
                          className="rounded-sm border-[#1C2A39] text-[#1C2A39] hover:bg-[#1C2A39] hover:text-white h-11 px-5 disabled:opacity-40"
                        >
                          <ArrowLeft size={16} strokeWidth={1.75} className="mr-2" />
                          Back
                        </Button>
                        <Button
                          type="button"
                          data-testid="boiler-next-button"
                          disabled={!selected}
                          onClick={goNext}
                          className="bg-[#C9A24A] hover:bg-[#B0893A] text-white rounded-sm h-11 px-6 font-heading font-semibold tracking-wide disabled:opacity-40 disabled:hover:bg-[#C9A24A]"
                        >
                          Continue
                          <ArrowRight size={16} strokeWidth={1.75} className="ml-2" />
                        </Button>
                      </div>
                    </>
                  )}
                  {step?.type === "contact" && (
                    <ContactStep
                      answers={answers}
                      setField={setField}
                      onNext={handleContactNext}
                      onBack={goBack}
                      submitting={submitting}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-6 lg:gap-10 text-xs text-[#5C6670]">
          <div className="flex items-center gap-2">
            <ShieldCheck size={14} strokeWidth={1.5} className="text-[#C9A24A]" />
            Gas Safe Registered
          </div>
          <div className="flex items-center gap-2">
            <Clock size={14} strokeWidth={1.5} className="text-[#C9A24A]" />
            Reply within 1 working day
          </div>
        </div>
      </div>
    </section>
  );
}
